// Post-login nudge that asks the user to register a passkey on this device.
//
// Show conditions (all must be true):
//   - Platform authenticator is available on this device (Face ID / Touch ID
//     / Windows Hello / Android biometrics). Desktops with no biometrics
//     get skipped so we don't dangle a dead prompt.
//   - The logged-in user has ZERO passkeys in their account. If they already
//     have one (on any device), we leave them alone — they know about
//     passkeys and the LoginPage "Sign in with passkey" button is enough.
//   - The user hasn't previously dismissed the nudge on THIS browser.
//     "Don't ask again" stores a localStorage flag so it never shows here.
//
// Positive path → opens the same registration flow as Settings. Failure or
// cancel closes silently; we're trying to help, not block.

import React, { useState, useEffect } from "react";
import { KeyRound, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { passkeysSupported, passkeysPlatformAvailable, registerPasskey } from "@/lib/passkeys";
import { useAltivModal } from "@/hooks/useAltivModal";

const API = `${import.meta.env.VITE_BACKEND_URL}/api`;
// Keys are scoped by email so that multiple accounts on the same browser
// don't inherit each other's dismissal state (same root bug as trusted-device).
function _normalize(email) {
  return (email || "").trim().toLowerCase();
}
function _key(base, email) {
  const e = _normalize(email);
  return e ? `${base}:${e}` : base; // fallback to global if no email (shouldn't happen)
}

const DISMISS_BASE = "altiv_passkey_nudge_dismissed";
const SESSION_BASE = "altiv_passkey_nudge_session";
const SNOOZE_BASE = "altiv_passkey_nudge_snooze_until";
// "Después" silencia el nudge por 7 días para que no reaparezca en cada
// login del día / navegación entre pantallas. Antes solo se cerraba en
// memoria y volvía a aparecer al siguiente cambio de ruta.
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

export default function PasskeyNudgeModal({ user, token }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      // Preconditions: logged in + browser support + not previously dismissed.
      if (!user || !token) return;
      if (!passkeysSupported()) return;
      const email = user?.email;
      const DISMISS_KEY = _key(DISMISS_BASE, email);
      const SESSION_KEY = _key(SESSION_BASE, email);
      const SNOOZE_KEY = _key(SNOOZE_BASE, email);
      if (localStorage.getItem(DISMISS_KEY) === "true") return;
      if (sessionStorage.getItem(SESSION_KEY) === "true") return;
      const snoozeUntil = Number(localStorage.getItem(SNOOZE_KEY) || 0);
      if (snoozeUntil > Date.now()) return;

      const platformOk = await passkeysPlatformAvailable();
      if (!alive || !platformOk) return;

      // Final gate: user must have zero passkeys. Cheap count endpoint.
      try {
        const res = await axios.get(`${API}/auth/passkey/count`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 8000,
        });
        if (alive && res.data?.count === 0) setVisible(true);
      } catch (e) {
        // If the count endpoint fails, don't nag — silently skip.
        console.warn("Passkey nudge count check failed:", e);
      }
    })();
    return () => {
      alive = false;
    };
  }, [user, token]);

  const handleAdd = async () => {
    setError("");
    setBusy(true);
    try {
      await registerPasskey();
      setSuccess(true);
      // Close after a brief success message so the user gets the confirmation.
      setTimeout(() => setVisible(false), 2200);
    } catch (err) {
      const name = err?.name || "";
      const msg = err?.message || "";
      if (name === "NotAllowedError" || msg.includes("cancelled")) {
        // User backed out — close without drama.
        setVisible(false);
      } else {
        setError(t("auth.passkey_nudge_failed"));
      }
    } finally {
      setBusy(false);
    }
  };

  const email = user?.email;
  const DISMISS_KEY = _key(DISMISS_BASE, email);
  const SESSION_KEY = _key(SESSION_BASE, email);
  const SNOOZE_KEY = _key(SNOOZE_BASE, email);

  const handleLater = () => {
    // Por-sesión + snooze 7 días para no reaparecer cada navegación.
    sessionStorage.setItem(SESSION_KEY, "true");
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    setVisible(false);
  };

  useAltivModal({ open: visible, onClose: handleLater });

  const handleNever = () => {
    localStorage.setItem(DISMISS_KEY, "true");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="altiv-modal-shell" role="dialog" aria-modal="true">
      <div className="altiv-modal-card sm:max-w-md">
        <div className="altiv-modal-header relative flex items-center justify-end px-4 sm:px-6 border-b border-slate-800">
          <button
            onClick={handleLater}
            className="p-2 text-slate-500 hover:text-white transition-colors"
            aria-label={t("common.close")}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="altiv-modal-body p-6">
          <div className="w-12 h-12 bg-blue-500/15 border border-blue-500/30 rounded-xl flex items-center justify-center mb-4">
            <KeyRound className="w-6 h-6 text-blue-400" />
          </div>

          {success ? (
            <>
              <h3 className="text-xl font-semibold text-white mb-2">
                {t("auth.passkey_nudge_success_title")}
              </h3>
              <p className="text-sm text-slate-400 mb-2">{t("auth.passkey_nudge_success_desc")}</p>
            </>
          ) : (
            <>
              <h3 className="text-xl font-semibold text-white mb-2">
                {t("auth.passkey_nudge_title")}
              </h3>
              <p className="text-sm text-slate-400 mb-5 leading-relaxed">
                {t("auth.passkey_nudge_desc")}
              </p>

              {error && (
                <div className="p-3 mb-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={handleAdd}
                  disabled={busy}
                  className="w-full btn-tier1 font-semibold py-3 rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {busy ? t("auth.passkey_nudge_adding") : t("auth.passkey_nudge_add")}
                </button>
                <button
                  type="button"
                  onClick={handleLater}
                  className="w-full py-2.5 rounded-md text-sm text-slate-400 hover:text-white transition-colors"
                >
                  {t("auth.passkey_nudge_later")}
                </button>
                <button
                  type="button"
                  onClick={handleNever}
                  className="w-full py-2.5 rounded-md text-sm font-medium text-slate-300 border border-slate-700 hover:bg-slate-800 hover:text-white transition-colors"
                >
                  {t("auth.passkey_nudge_never")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
